import React, { useEffect, useMemo, useRef, useState } from 'react';
import { sendFinanceChat, fetchFinanceSuggestions } from '../../utils/financeBalancesApi';
import '../../Styles/FinancialBalancesChatWidget.css';

const STORAGE_KEY = 'fnb_widget_state_v1';

const WELCOME = {
  id: 'welcome',
  sender: 'ai',
  text: 'Hi! I am your Finance AI Assistant. Ask me about ledger balances, vendor payables, customer receivables, and aging analytics.',
};

function nowLabel() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function buildSessionId() {
  return `fnb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function makeTitle(question) {
  const clean = (question || '').trim();
  if (!clean) return 'New chat';
  return clean.length > 30 ? `${clean.slice(0, 30)}…` : clean;
}

function buildSession(title = 'New chat') {
  return {
    id: buildSessionId(),
    title,
    updatedAt: Date.now(),
    serverSessionId: null,
    messages: [WELCOME],
  };
}

function getInitialState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const s = buildSession('Chat 1');
      return { open: false, expanded: false, fullScreen: false, activeSessionId: s.id, sessions: [s] };
    }
    const p = JSON.parse(raw);
    const sessions = Array.isArray(p.sessions) && p.sessions.length > 0 ? p.sessions : [buildSession('Chat 1')];
    const activeSessionId = sessions.some((s) => s.id === p.activeSessionId) ? p.activeSessionId : sessions[0].id;
    return {
      open: typeof p.open === 'boolean' ? p.open : false,
      expanded: typeof p.expanded === 'boolean' ? p.expanded : false,
      fullScreen: typeof p.fullScreen === 'boolean' ? p.fullScreen : false,
      activeSessionId,
      sessions,
    };
  } catch {
    const s = buildSession('Chat 1');
    return { open: false, expanded: false, fullScreen: false, activeSessionId: s.id, sessions: [s] };
  }
}

export default function FinancialBalancesChatWidget() {
  const initial = useMemo(() => getInitialState(), []);
  const [open, setOpen] = useState(initial.open);
  const [expanded, setExpanded] = useState(initial.expanded);
  const [fullScreen, setFullScreen] = useState(initial.fullScreen);
  const [sessions, setSessions] = useState(initial.sessions);
  const [activeSessionId, setActiveSessionId] = useState(initial.activeSessionId);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [starterSuggestions, setStarterSuggestions] = useState([]);
  const [starterClarification, setStarterClarification] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    fetchFinanceSuggestions()
      .then((res) => {
        setStarterSuggestions(res.suggestions || []);
        setStarterClarification(res.clarification_prompt || null);
      })
      .catch(() => {});
  }, []);

  const activeSession = useMemo(
    () => sessions.find((s) => s.id === activeSessionId) || sessions[0],
    [sessions, activeSessionId]
  );
  const messages = activeSession?.messages || [WELCOME];

  // Persist state
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ open, expanded, fullScreen, activeSessionId, sessions }));
  }, [open, expanded, fullScreen, activeSessionId, sessions]);

  // Escape exits fullscreen
  useEffect(() => {
    if (!fullScreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setFullScreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullScreen]);

  // Body class for fullscreen scroll lock
  useEffect(() => {
    document.body.classList.toggle('fnb-widget-fullscreen-active', fullScreen);
    return () => document.body.classList.remove('fnb-widget-fullscreen-active');
  }, [fullScreen]);

  // Auto-scroll
  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [open, messages, loading]);

  const canSend = useMemo(() => input.trim().length > 0 && !loading, [input, loading]);

  const updateActive = (patch) => {
    setSessions((prev) => prev.map((s) =>
      s.id === activeSessionId ? { ...s, ...patch, updatedAt: Date.now() } : s
    ));
  };

  const sendMessage = async (question) => {
    const clean = (question || '').trim();
    if (!clean || loading) return;

    const userMsg = { id: `u-${Date.now()}`, sender: 'user', text: clean, time: nowLabel() };
    const nextTitle = messages.length <= 1 ? makeTitle(clean) : activeSession?.title;
    const nextMessages = [...messages, userMsg];

    updateActive({ title: nextTitle, messages: nextMessages });
    setInput('');
    setError('');
    setLoading(true);

    try {
      const payload = { message: clean };
      if (activeSession?.serverSessionId) payload.session_id = activeSession.serverSessionId;
      const res = await sendFinanceChat(payload);
      const aiMsg = {
        id: `a-${Date.now()}`,
        sender: 'ai',
        text: res.reply,
        time: nowLabel(),
        suggestions: res.follow_up_suggestions || [],
        data: res.data || null,
      };
      updateActive({
        messages: [...nextMessages, aiMsg],
        ...(res.session_id && { serverSessionId: res.session_id }),
      });
    } catch (err) {
      setError(err.message || 'Failed to get a response.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => { e.preventDefault(); if (canSend) sendMessage(input); };
  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (canSend) sendMessage(input); } };

  const handleNewChat = () => {
    const s = buildSession(`Chat ${sessions.length + 1}`);
    setSessions((prev) => [s, ...prev]);
    setActiveSessionId(s.id);
    setInput('');
    setError('');
  };

  const handleClearChat = () => {
    updateActive({ title: 'New chat', messages: [WELCOME], serverSessionId: null });
    setError('');
  };

  const handleStartRename = (s) => { setEditingSessionId(s.id); setRenameValue(s.title || 'New chat'); };
  const handleCancelRename = () => { setEditingSessionId(null); setRenameValue(''); };
  const handleSaveRename = (id) => {
    const t = renameValue.trim();
    if (!t) return;
    setSessions((prev) => prev.map((s) => s.id === id ? { ...s, title: t, updatedAt: Date.now() } : s));
    handleCancelRename();
  };
  const handleDeleteSession = (id) => {
    setSessions((prev) => {
      const rest = prev.filter((s) => s.id !== id);
      if (rest.length === 0) { const fb = buildSession('Chat 1'); setActiveSessionId(fb.id); return [fb]; }
      if (activeSessionId === id) setActiveSessionId(rest[0].id);
      return rest;
    });
    if (editingSessionId === id) handleCancelRename();
  };

  const sortedSessions = useMemo(
    () => [...sessions].sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0)),
    [sessions]
  );

  return (
    <div className={`fnb-widget-container${open ? ' is-open' : ''}${fullScreen ? ' fullscreen' : ''}`}>
      {/* Toggle pill */}
      {!open && (
        <button
          type="button"
          className="fnb-widget-toggle"
          onClick={() => setOpen(true)}
          aria-label="Open Finance AI assistant"
        >
          Finance AI
        </button>
      )}

      {/* Panel */}
      {open && (
        <section className={`fnb-widget-panel${expanded ? ' expanded' : ''}`} aria-label="Finance AI Chat">
          {/* Header */}
          <header className="fnb-widget-header">
            <div className="fnb-widget-title">
              <h3>Finance AI</h3>
              <p>{loading ? 'Analyzing…' : 'Ledger · Vendor · Customer'}</p>
            </div>
            <div className="fnb-widget-actions">
              <button type="button" onClick={handleNewChat}>New</button>
              <button type="button" onClick={handleClearChat}>Clear</button>
              <button type="button" onClick={() => setExpanded((p) => !p)}>{expanded ? 'Min' : 'Max'}</button>
              {fullScreen
                ? <button type="button" onClick={() => setFullScreen(false)}>Exit</button>
                : <button type="button" onClick={() => { setExpanded(true); setFullScreen(true); }}>Full</button>
              }
              <button type="button" onClick={() => { setOpen(false); setFullScreen(false); }}>Close</button>
            </div>
          </header>

          {/* Body */}
          <div className="fnb-widget-body">
            {/* Sessions sidebar */}
            {expanded && (
              <aside className="fnb-widget-sessions" aria-label="Chat sessions">
                <h4>Sessions</h4>
                <div className="fnb-widget-session-list">
                  {sortedSessions.map((s) => (
                    <div key={s.id} className={`fnb-widget-session-item${s.id === activeSessionId ? ' active' : ''}`}>
                      {editingSessionId === s.id ? (
                        <div className="fnb-widget-session-rename" onClick={(e) => e.stopPropagation()}>
                          <input
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); handleSaveRename(s.id); }
                              if (e.key === 'Escape') { e.preventDefault(); handleCancelRename(); }
                            }}
                            autoFocus
                          />
                          <div className="fnb-widget-session-rename-actions">
                            <button type="button" onClick={() => handleSaveRename(s.id)}>Save</button>
                            <button type="button" onClick={handleCancelRename}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="fnb-widget-session-main"
                            onClick={() => { setActiveSessionId(s.id); setError(''); }}
                          >
                            <strong>{s.title || 'New chat'}</strong>
                            <span>{new Date(s.updatedAt || Date.now()).toLocaleString([], { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                          </button>
                          <div className="fnb-widget-session-item-actions">
                            <button type="button" onClick={() => handleStartRename(s)}>Edit</button>
                            <button type="button" onClick={() => handleDeleteSession(s.id)}>Del</button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </aside>
            )}

            {/* Main chat */}
            <div className="fnb-widget-main">
              <div className="fnb-widget-messages">
                {/* Starter suggestion chips — only before first user message */}
                {messages.length <= 1 && starterSuggestions.length > 0 && (
                  <div className="fnb-widget-quick-prompts">
                    {starterClarification && (
                      <p className="fnb-widget-clarification">{starterClarification}</p>
                    )}
                    {starterSuggestions.map((s) => (
                      <button key={s} type="button" onClick={() => sendMessage(s)}>{s}</button>
                    ))}
                  </div>
                )}

                {messages.map((m) => (
                  <article key={m.id} className={`fnb-widget-message${m.sender === 'user' ? ' user' : ' ai'}`}>
                    <div className="fnb-widget-bubble">
                      <p>{m.text}</p>
                      {m.time && <small className="fnb-widget-time">{m.time}</small>}
                    </div>
                    {m.sender === 'ai' && m.suggestions?.length > 0 && (
                      <div className="fnb-widget-suggestions">
                        {m.suggestions.map((s, i) => (
                          <button key={i} type="button" onClick={() => sendMessage(s)}>{s}</button>
                        ))}
                      </div>
                    )}
                  </article>
                ))}

                {loading && (
                  <div className="fnb-widget-typing">
                    <span /><span /><span />
                  </div>
                )}
                <div ref={endRef} />
              </div>

              {error && <p className="fnb-widget-error">{error}</p>}

              <form className="fnb-widget-input" onSubmit={handleSubmit}>
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={2}
                  placeholder="Ask about ledger balance, vendor payables, customer receivables…"
                />
                <button type="submit" disabled={!canSend}>
                  {loading ? 'Sending…' : 'Send'}
                </button>
              </form>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
